import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { createRef, useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import { unwrapResult } from '@reduxjs/toolkit';
import { AccountInfo } from '.';
import { fromAccounts, fromSubscription, useAppDispatch } from '../../store';
import { LoadingProgress } from '../../layout';
import { useAccount } from '../../account';
import { homePage, redirectToLogoutPage, useNavigate, useQuery } from '../../routes';
import { checkout, plan, subscriptionManagement } from '../../layout/routes';
import { Account, SubscriptionSource } from '../../models';
import { useParseQueryParam } from './hook/useParseQueryParam';
import { ACCOUNT_SELECTION_ROUTE_QUERY_PARAM } from '../../utils/Constants';

const useStyle = makeStyles(theme => ({
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(3),
    backgroundColor: '#F7F9F8',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 720,
    maxHeight: '90%',
    minHeight: 480,
    padding: theme.spacing(4),
    backgroundColor: '#fff',
    border: '1px solid #E1E7E4',
    borderRadius: 16,
    boxShadow: '0 1px 2px rgba(22, 20, 29, 0.04)',
  },
  title: {
    margin: 0,
    fontSize: 24,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: '#16141D',
  },
  subtitle: {
    margin: theme.spacing(0.875, 0, 0),
    fontSize: 13.8,
    lineHeight: 1.55,
    color: '#5F5D75',
  },
  list: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    overflowY: 'auto',
    margin: theme.spacing(3, 0),
    // room for the focus ring / hover border of the edge items
    padding: theme.spacing(0.5),
    scrollbarWidth: 'thin',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(3),
    borderTop: '1px solid #EEF2F0',
  },
  backBtn: {
    height: 44,
    padding: theme.spacing(0, 2.5),
    border: '1.5px solid #D7DBE0',
    borderRadius: '10px',
    backgroundColor: '#fff',
    color: '#16141D',
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none' as const,
    '&:hover': {
      borderColor: '#B6BCC4',
      backgroundColor: '#F7F7FB',
    },
  },
  continueBtn: {
    height: 44,
    padding: theme.spacing(0, 2.75),
    border: 'none',
    borderRadius: '10px',
    backgroundColor: '#0F8461',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none' as const,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#0B6B4E',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#0F8461',
      color: '#fff',
      opacity: 0.5,
    },
  },
}));

export const AccountSelection = () => {
  const classes = useStyle();
  const dispatch = useAppDispatch();
  const { accounts, loading } = useAccount();
  const { navigate, replaceQuery } = useNavigate();
  const { get } = useQuery();
  const route = get(ACCOUNT_SELECTION_ROUTE_QUERY_PARAM);
  const spEnabled = get('spEnabled') === 'true';
  const [optedInStarterPackage, setOptedInStarterPackage] = useState(false);
  const accLoading = useSelector(fromAccounts.selectLoading);
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const [redirecting, setRedirecting] = useState(false);
  const accountRefs = useRef<any[]>([]);
  const queryParam = useParseQueryParam(selectedAccount);
  const subs = useSelector(
    fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
  );
  accountRefs.current = accounts.map((_, index) => accountRefs.current[index] ?? createRef());

  const onAccountSelect = (account: Account) => {
    const checkStarterPackage = async (accountId: number) => {
      await dispatch(fromAccounts.doCheckStarterPackage({ accountId, spEnabled }))
        .then(unwrapResult)
        .then(isOptedInStarterPackage => {
          setOptedInStarterPackage(isOptedInStarterPackage);
        })
        .catch(() => {
          // do nothing
        });
    };

    checkStarterPackage(account.id)
      .finally(() => setRedirecting(true));
  };

  useEffect(() => {
    if (accounts.length === 1) {
      setSelectedAccount(accounts[0]);
      onAccountSelect(accounts[0]);
    }
    // when there's account is not finish checking permission,
    // we don't want to auto select the account
    if (accountRefs.current.some(ref => ref.current === null || ref.current.loading !== false)) {
      return;
    }

    // filter out the account that is not allowed
    const allowedAccount = accounts
      .filter((_, index) => accountRefs.current[index].current.allowed);

    // if there's only one allowed account, auto select this account
    if (allowedAccount.length === 1) {
      setSelectedAccount(allowedAccount[0]);
      onAccountSelect(allowedAccount[0]);
    }
  }, [accounts, accountRefs]);

  useEffect(() => {
    if (!accLoading && redirecting && selectedAccount) {
      if (optedInStarterPackage) {
        navigate(
          route === checkout.path
            ? `${subscriptionManagement.path}/purchase`
            : subscriptionManagement.path,
          replaceQuery(queryParam),
        );
        return;
      }

      navigate(
        route ?? (subs.length > 0 ? plan.path : homePage.path),
        replaceQuery(queryParam),
      );
    }
  }, [redirecting, optedInStarterPackage, accLoading]);

  const onBack = () => redirectToLogoutPage();

  const onContinue = () => {
    if (selectedAccount) {
      onAccountSelect(selectedAccount);
    }
  };

  return loading ? <LoadingProgress fullscreen /> : (
    <>
      <Box id="account-selection-container" className={classes.page}>
        <Box id="account-selection-box" className={classes.card}>
          <Box id="account-selection-title">
            <Typography component="h1" className={classes.title}>
              <FormattedMessage id="page.account.selection.title" />
            </Typography>
            <Typography component="p" className={classes.subtitle}>
              <FormattedMessage id="page.account.selection.sub.title" />
            </Typography>
          </Box>
          <Box
            id="account-selection-list"
            role="listbox"
            aria-labelledby="account-selection-title"
            className={classes.list}
          >
            {accounts.map((account, index) => {
              const selected = selectedAccount?.id === account.id;
              return (
                <AccountInfo
                  key={`account-selection-account-info-${account.id}`}
                  ref={accountRefs.current[index]}
                  selected={selected}
                  account={account}
                  onSelectAccount={setSelectedAccount}
                />
              );
            })}
          </Box>
          <Box id="account-selection-footer" className={classes.footer}>
            <Button
              variant="outlined"
              className={classes.backBtn}
              onClick={onBack}
            >
              <FormattedMessage id="page.account.selection.button.back" />
            </Button>
            <Button
              variant="contained"
              className={classes.continueBtn}
              disabled={!selectedAccount}
              onClick={onContinue}
            >
              <FormattedMessage id="page.account.selection.button.continue" />
            </Button>
          </Box>
        </Box>
      </Box>
      {redirecting && (<LoadingProgress fullscreen />)}
    </>
  );
};
